import { z } from "zod";
import { Id } from "./common.ts";
import { ProposeActionInput, type CopilotReadToolT } from "./copilot.ts";
import { DatasetName, Dimension, Filter, Measure, Sort, TimePreset } from "./dashboards.ts";
import { AiLabels, FlagKey } from "./domain.ts";

/**
 * The Copilot's tools (plan 14 §5), as data: the API binds `/v1/copilot/tools/<name>` from this list,
 * `/v1/copilot/tools/openapi.json` is generated from it, and Dify's custom tool provider "Malfak" is
 * registered from that spec by `tools/dify-init.mjs`. Inputs are flat and LLM-friendly.
 *
 * Auth: every call carries the scoped tool token in `X-Copilot-Token` (injected by the Dify Agent node from the
 * conversation inputs, never typed by the model) plus the provider's static `X-Malfak-Tool-Key`.
 */
const Sentiment = AiLabels.shape.sentiment.unwrap();
const Intent = AiLabels.shape.intent.unwrap();
const Range = TimePreset.exclude(["custom"]);
/**
 * Copilot time windows: the default is ALWAYS all time (no hidden window). A period is applied only when the user asks
 * for one, and every time-filtered result reports the window it used (`data.window`) so the answer can state it.
 */
const TimeWindow = Range.default("all").describe("Time window. Default all = no time filter. Set it ONLY when the user asks for a period (today, 24h, 7d, 30d, 90d, mtd = this month, ytd = this year).");
/**
 * ALP-83: exact dates. `from` / `to` = YYYY-MM-DD (a whole day in the org's timezone; `to` is inclusive) or an ISO
 * instant. Either one switches the window to the analytics `custom` preset and wins over `range`; a missing `from`
 * means "since the beginning", a missing `to` means "until now". Long spans follow the `all` rules (no maxRangeDays,
 * coarser buckets). For named periods ("during Ramadan", "national day week") resolve the dates with `calendar_events`
 * first. The result's `window` always states the exact dates used.
 */
export const CopilotDate = z.string().regex(/^\d{4}-\d{2}-\d{2}([T ][0-9:.]+(Z|[+-]\d{2}:?\d{2})?)?$/, "YYYY-MM-DD or an ISO date-time");
const DATE_DESC = "Exact start date (YYYY-MM-DD, org timezone). Setting from/to overrides range.";
const dates = {
  from: CopilotDate.optional().describe(DATE_DESC),
  to: CopilotDate.optional().describe("Exact end date, inclusive (YYYY-MM-DD, org timezone). Setting from/to overrides range."),
};
/** `from` must not be after `to` (applied by the API before running a tool; exported for tests). */
export function checkCopilotDates(i: { from?: string | undefined; to?: string | undefined }): string | null {
  if (i.from && i.to && i.from.slice(0, 10) > i.to.slice(0, 10)) return "from must be on or before to";
  return null;
}
/** Tags filter: built-in flags, custom tags or AI categories (any of them). */
const tagsFilter = () => z.array(FlagKey).max(10).optional().describe("Built-in flags, custom tags or AI categories (keys); items carrying ANY of them");
const ruleFilter = () => Id.optional().describe("Only items a rule tagged (entity_flags added by this rule id); \"what did rule X tag\"");

/** A post id, or `latest` = the newest published post of the org (or of `accountId`). */
export const CopilotPostRef = z.union([Id, z.literal("latest")]);
const postRef = () => CopilotPostRef.describe('A post id, or "latest" for the newest published post ("the last post"), regardless of its age');
const Limit = (d: number, max = 50) => z.number().int().min(1).max(max).default(d);

export const ToolInputs = {
  search_comments: z.object({
    query: z.string().min(1).max(200).describe("What to look for, in the user's words (any language)"),
    mode: z.enum(["auto", "keyword", "semantic"]).default("auto").describe("semantic = by meaning (Dify Knowledge); keyword = exact words; auto = semantic when available"),
    sentiment: Sentiment.optional(),
    intent: Intent.optional(),
    postId: postRef().optional(),
    accountId: Id.optional(),
    includeDeleted: z.boolean().default(false),
    range: TimeWindow,
    ...dates,
    tags: tagsFilter(),
    taggedByRule: ruleFilter(),
    limit: Limit(20),
  }),
  list_comments: z.object({
    sentiment: Sentiment.optional(),
    intent: Intent.optional(),
    postId: postRef().optional(),
    accountId: Id.optional(),
    status: z.enum(["visible", "hidden", "deleted"]).optional(),
    flag: FlagKey.optional(),
    replied: z.boolean().optional().describe("false = the account has not answered yet"),
    range: TimeWindow,
    ...dates,
    tags: tagsFilter(),
    taggedByRule: ruleFilter(),
    sort: z.enum(["newest", "toxicity", "replies"]).default("newest"),
    limit: Limit(20),
  }),
  list_deleted: z.object({ kind: z.enum(["comments", "messages"]).default("comments"), range: TimeWindow, ...dates, limit: Limit(20) }),
  get_post: z.object({ postId: postRef(), accountId: Id.optional().describe('With postId "latest": the newest post of this account') }),
  list_posts: z.object({
    accountId: Id.optional(),
    range: TimeWindow,
    ...dates,
    status: z.enum(["published", "scheduled", "draft", "any"]).default("published"),
    sort: z.enum(["recent", "comments", "likes"]).default("recent").describe("recent = newest first."),
    limit: Limit(12, 30),
  }),
  // ALP-83: posts with their (filtered) comments nested: feeds the `posts_with_comments` block
  list_posts_with_comments: z.object({
    postIds: z.array(postRef()).max(10).optional().describe('Specific posts (ids or "latest"); omit to take the newest `latest` published posts'),
    latest: z.number().int().min(1).max(10).default(3).describe("How many of the newest posts when postIds is omitted"),
    accountId: Id.optional(),
    sentiment: Sentiment.optional(),
    intent: Intent.optional(),
    tags: tagsFilter(),
    taggedByRule: ruleFilter(),
    status: z.enum(["visible", "hidden", "deleted"]).optional(),
    query: z.string().max(200).optional().describe("Words (keyword) or a meaning (semantic) the comments must match"),
    mode: z.enum(["auto", "keyword", "semantic"]).default("auto"),
    replied: z.boolean().optional(),
    range: TimeWindow.describe("Comment time window (posts are picked regardless of age). Default all."),
    ...dates,
    perPost: Limit(5, 20).describe("Comments shown per post (the total matching is reported too)"),
  }),
  post_insights: z.object({ postId: postRef(), accountId: Id.optional() }),
  account_analytics: z.object({
    title: z.string().min(1).max(120).describe("Chart/KPI title in the user's language"),
    viz: z.enum(["kpi", "line", "bar", "area", "donut", "heatmap", "table"]).describe("kpi = numbers; line/area/bar over time; donut = shares; heatmap = hour_of_day × day_of_week"),
    dataset: DatasetName,
    measures: z.array(Measure).min(1).max(4),
    dimensions: z.array(Dimension).max(2).default([]),
    filters: z.array(Filter).max(8).default([]),
    range: TimeWindow,
    ...dates,
    compare: z.boolean().default(false).describe("Compare with the previous period (KPIs; needs a range other than all)"),
    sort: z.array(Sort).max(2).optional(),
    limit: z.number().int().min(1).max(200).optional(),
  }),
  best_times: z.object({
    accountId: Id.optional(),
    range: Range.optional().describe("Activity window. Default: the last 90 days (audience habits change), or all time when there was no activity in them"),
  }),
  list_conversations: z.object({
    state: z.enum(["open", "done", "snoozed"]).optional(),
    sentiment: Sentiment.optional(),
    intent: Intent.optional(),
    windowOpen: z.boolean().optional().describe("true = the 24h reply window is still open"),
    tags: tagsFilter(),
    taggedByRule: ruleFilter(),
    range: TimeWindow,
    ...dates,
    limit: Limit(15),
  }),
  get_contact: z.object({ contactId: Id }),
  list_people: z.object({
    accountId: Id.optional(),
    mark: z.enum(["harmful", "troll", "vip", "any", "none"]).optional(),
    tag: FlagKey.optional().describe("People with at least one comment carrying this flag, tag or AI category (or flagged themselves)"),
    sort: z.enum(["recent", "comments", "negative", "toxic"]).default("negative"),
    q: z.string().max(100).optional(),
    limit: Limit(15),
  }),
  list_rules: z.object({}),
  simulate_rule: z.object({ ruleId: Id, sampleSize: z.number().int().min(1).max(100).default(30) }),
  list_jobs: z.object({ status: z.enum(["active", "finished", "failed", "all"]).default("all"), limit: Limit(10, 30) }),
  sync_health: z.object({ accountId: Id.optional() }),
  search_knowledge: z.object({ query: z.string().min(1).max(300).describe("Question about the business (products, prices, policies, FAQ)"), limit: Limit(5, 10) }),
  describe_datasets: z.object({ dataset: DatasetName.optional() }),
  // ALP-80: the org's calendar subscription (countries, categories, moods) filters the events; read-only
  calendar_events: z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe("YYYY-MM-DD, default today"),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe("YYYY-MM-DD, default from + 30 days (max 366 days)"),
    country: z.string().regex(/^([A-Za-z]{2}|global)$/).optional().describe("ISO 3166-1 alpha-2 (SA, AE, EG…) or global; only within the subscription"),
    type: z.enum(["public_holiday", "national_day", "religious", "observance", "international_day", "crisis", "global_event"]).optional().describe("crisis = active crises/alerts"),
    mood: z.enum(["celebratory", "neutral", "solemn", "sensitive"]).optional(),
    accountId: Id.optional().describe("Use this account's calendar override instead of the org subscription"),
    limit: Limit(20),
  }),
  propose_action: ProposeActionInput,
} as const satisfies Record<CopilotReadToolT | "propose_action", z.ZodTypeAny>;

export type CopilotToolName = keyof typeof ToolInputs;
export const COPILOT_TOOL_NAMES = Object.keys(ToolInputs) as CopilotToolName[];

/** LLM-facing descriptions (English; the model answers in the user's language). */
export const TOOL_DESCRIPTIONS: Record<CopilotToolName, string> = {
  search_comments: "Search comments by words or by meaning (semantic search over the org's Dify Knowledge index). All time unless `range` is set. Optional tags / AI categories, taggedByRule and exact from/to dates. Returns matching comments, `window` and a `comments` block ref.",
  list_comments: "List comments filtered by sentiment, intent, post, status, flag, replied, time range. `tags` = built-in flags, custom tags or AI categories (any of; e.g. [\"positive\"]), `taggedByRule` = what a rule tagged; `from`/`to` = exact dates (YYYY-MM-DD, inclusive, org timezone), e.g. \"between 1 and 15 March\" → from 2026-03-01, to 2026-03-15. All time unless `range` is set; postId \"latest\" = the comments of the newest post (\"the last post\"), whatever its age. Returns comments, `window`, a `comments` block ref and a `chart` block ref (comments over time). A `hint` means matching comments exist outside the filters: say so.",
  list_deleted: "Comments or DMs that were deleted/unsent on the channel (Malfak keeps their text). All time unless `range` is set (or exact from/to). Returns items, `window` and a block ref.",
  get_post: "One post with its caption and publications; postId \"latest\" = the newest published post (\"the last post\"), whatever its age. Returns a `posts` block ref.",
  list_posts: "Posts of the org (optionally one account), newest first (sort recent) or most engaging. All time and published only unless set; limit 1 + sort recent = the last post. Returns items, `window` and a `posts` block ref.",
  list_posts_with_comments: "The newest N posts (default 3) or given posts, each with ITS comments filtered by sentiment, intent, tags / AI categories, rule, status, words/meaning and dates. Use for any question mixing posts and comments (\"the last three posts with their negative comments\"). Returns groups (postId, commentIds, totalMatching), `window` and a `posts_with_comments` block ref.",
  post_insights: "Lifetime insights of one post (reach, views, likes, saves, shares, comments over its whole life); postId \"latest\" = the newest post. Returns `stats` + `chart` block refs.",
  account_analytics: "Run an analytics query (semantic layer; see describe_datasets) and get a live `chart`/`stats`/`table` block ref. Use for any number, trend or breakdown. Filters may use `tag` (flags, custom tags, AI categories) and `tagged_by_rule`; `from`/`to` give exact dates. All time unless `range` is set (long trends come back in weekly/monthly buckets). Returns `window`.",
  best_times: "Best hours/days to post (heatmap) from the audience's comment activity: the last 90 days, or all time when they were empty (see `window`). Returns a `chart` block ref (heatmap).",
  list_conversations: "DM conversations filtered by state, sentiment, intent, open reply window, tags / AI categories, taggedByRule and the last-message window (range or from/to). All time unless set. Returns items, `window` and a `conversations` block ref.",
  get_contact: "A contact / commenter profile with marks, flags and counts. Returns a `contact` block ref.",
  list_people: "People who commented, sorted by negativity/toxicity/volume; find harmful commenters; `tag` = people with a comment carrying that tag / AI category (e.g. lead). Returns a `table` block ref.",
  list_rules: "Automation rules (name, trigger, mode, enabled).",
  simulate_rule: "Dry-run an existing rule against recent comments (what it would do).",
  list_jobs: "Long-running jobs (syncs, reloads, bulk actions) with progress. Returns `job` block refs.",
  sync_health: "Webhook/reconciliation health per account: last webhook, last reconcile, recovered items, open incidents (missed webhooks).",
  search_knowledge: "Search the business's own documents (brand, products, prices, policies, FAQ) uploaded to Dify Knowledge.",
  describe_datasets: "The analytics catalog: datasets, fields, allowed measures and filters. Call before account_analytics when unsure.",
  calendar_events: "Upcoming national/religious/international days, public holidays and crises from the org's calendar subscription (filter by country, type, mood, date range). Use for 'what national days are coming?' (type national_day) or 'any crisis this week?' (type crisis, to = +7 days). Without from/to the window is today → +30 days; the result's `window` says which dates were searched (state them). Returns events and a `timeline` block ref.",
  propose_action: "Propose a WRITE (hide/delete/reply/flag (built-in flags, custom tags, AI categories)/archive/pin/mark/reload/resync/rule/post, agent_reply = an AI agent drafts replies for these comments/conversations (each becomes an approval), assign = assign comments to a teammate). Target the ids a read tool returned. Never executes: the user reviews and confirms. Returns `proposalId` to put in `proposalIds`.",
};

/** Read tools a viewer may call; `propose_action` needs the `propose` scope (editor+). */
export const READ_TOOLS = COPILOT_TOOL_NAMES.filter((n) => n !== "propose_action");

export const ProposeActionResult = z.object({ proposalId: Id, kind: z.string(), items: z.number(), blocked: z.number(), title: z.string() });

export type ToolInput<N extends CopilotToolName> = z.output<(typeof ToolInputs)[N]>;
